import { useQueryClient } from '@tanstack/react-query';
import { useCompany } from '@/contexts/CompanyContext';
import { graphqlQuery } from '@/lib/api/graphql';
import {
  mapTemplateDetailApiToUi,
  mapTemplateListItemApiToUi,
  mapTemplateUiToCreateRequest,
  mapTemplateUiToUpdateRequest,
} from '@/lib/mappers/templates';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import { useMode } from '@/providers/ModeProvider';
import type {
  CreateTemplateApiRequest,
  GetTemplatesApiResponse,
  TemplateDetailApi,
  UpdateTemplateApiRequest,
} from '@/types/api/templates';
import type { Template } from '@/types/template';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';

export interface GetTemplatesParams extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
}

const mockTemplates: Template[] = [
  {
    id: '1',
    name: 'Payment Reminder',
    type: 'email',
    subject: 'Payment Reminder - Account <<accountNumber>>',
    content:
      '<p>Dear <<customerName>>,</p><p>This is a friendly reminder that your payment of <<accountBalance>> is due on <<dueDate>>.</p><p>Best regards,<br><<companyName>></p>',
    isDefault: true,
    category: ['payment', 'reminder'],
    variables: ['<<customerName>>', '<<accountNumber>>', '<<accountBalance>>', '<<dueDate>>', '<<companyName>>'],
    lastModified: '2024-01-15T10:00:00Z',
    createdAt: '2024-01-01T10:00:00Z',
    createdBy: 'Admin User',
  },
  {
    id: '2',
    name: 'Welcome Email',
    type: 'email',
    subject: 'Welcome to <<companyName>>',
    content:
      '<p>Dear <<customerName>>,</p><p>Welcome to <<companyName>>! We are excited to have you as a customer.</p><p>Your account number is <<accountNumber>>.</p>',
    isDefault: false,
    category: ['welcome', 'onboarding'],
    variables: ['<<customerName>>', '<<accountNumber>>', '<<companyName>>'],
    lastModified: '2024-01-20T14:30:00Z',
    createdAt: '2024-01-10T09:00:00Z',
    createdBy: 'Admin User',
  },
  {
    id: '3',
    name: 'Monthly Statement',
    type: 'statement',
    content:
      '<h1>Account Statement</h1><p>Account: <<accountNumber>></p><p>Customer: <<customerName>></p><p>Balance: <<accountBalance>></p><p>Due Date: <<dueDate>></p>',
    isDefault: true,
    category: ['monthly', 'statement'],
    variables: ['<<customerName>>', '<<accountNumber>>', '<<accountBalance>>', '<<dueDate>>'],
    lastModified: '2024-01-18T11:00:00Z',
    createdAt: '2024-01-05T10:00:00Z',
    createdBy: 'Admin User',
    pdfSettings: {
      templateType: 'detailed',
      companyName: 'Acme Corp',
      logoSize: 'medium',
      styling: {
        fontFamily: 'Arial',
        fontSize: 12,
        primaryColor: '#1a5c5c',
        secondaryColor: '#e5e7eb',
        lineSpacing: 1.5,
      },
      logo: {
        url: 'https://via.placeholder.com/150',
        position: 'top-left',
        width: 150,
        height: 50,
      },
      layoutSettings: {
        pageSize: 'a4',
        orientation: 'portrait',
        margins: 'normal',
        headerLayout: {
          left: ['logo'],
          center: ['empty'],
          right: ['company-name'],
          showDivider: true,
          dividerThickness: '1',
          dividerColor: '#e5e7eb',
          repeatOnAllPages: true,
        },
        footer: {
          enabled: true,
          showPaymentInstructions: true,
          showCompanyContact: true,
          showDivider: true,
          repeatOnAllPages: true,
        },
        pageNumbers: {
          enabled: true,
          position: 'bottom-center',
          format: 'page-of',
        },
      },
      contentSettings: {
        documentTitle: 'Monthly Statement',
        customerAddress: {
          enabled: true,
          content:
            '<<customerName>>\n<<customerAddressLine>>\n<<customerCity>>\n<<customerCounty>>\n<<customerPostcode>>\n<<customerCountry>>',
        },
        referenceBlock: {
          enabled: true,
          items: {
            accountNumber: true,
            paymentTerms: true,
            statementDate: true,
            documentReference: true,
          },
        },
        introText: {
          enabled: true,
          content: 'Please find below your monthly statement.',
        },
        paymentInstructions: {
          enabled: true,
          content: 'Please make payment to the account details shown below.',
        },
        closingText: {
          enabled: true,
          content: 'Thank you for your business.',
        },
      },
      fieldsSettings: {
        accountSummary: {
          enabled: true,
          items: {
            totalOutstanding: true,
            totalOverdue: true,
            totalNotYetDue: true,
            creditLimit: true,
            availableCredit: true,
            lastPaymentAmount: true,
            lastPaymentDate: true,
          },
        },
        transactionTable: {
          columns: [
            {
              id: 'invoice_no',
              name: 'Reference Number',
              variable: '{{reference_number}}',
              enabled: true,
            },
            {
              id: 'invoice_date',
              name: 'Transaction Date',
              variable: '{{transaction_date}}',
              enabled: true,
            },
            {
              id: 'due_date',
              name: 'Due Date',
              variable: '{{due_date}}',
              enabled: true,
            },
            {
              id: 'description',
              name: 'Description',
              variable: '{{transaction_description}}',
              enabled: true,
            },
            {
              id: 'invoice_amount',
              name: 'Gross Amount',
              variable: '{{gross_amount}}',
              enabled: true,
            },
            {
              id: 'payments_received',
              name: 'Payments Received',
              variable: '{{payments_received}}',
              enabled: true,
            },
            {
              id: 'balance_outstanding',
              name: 'Outstanding Amount',
              variable: '{{outstanding_amount}}',
              enabled: true,
            },
            {
              id: 'days_overdue',
              name: 'Days Overdue',
              variable: '{{days_overdue}}',
              enabled: true,
            },
            {
              id: 'po_number',
              name: 'Order Number',
              variable: '{{order_number}}',
              enabled: false,
            },
            {
              id: 'transaction_type',
              name: 'Type',
              variable: '{{transaction_type}}',
              enabled: false,
            },
          ],
          displayOptions: {
            showBorders: true,
            zebraStripe: false,
            highlightOverdue: true,
            boldOverdue: true,
          },
          sortBy: 'oldest-first',
          filter: 'all',
        },
        totals: {
          items: {
            totalInvoiced: true,
            totalOutstanding: true,
            totalOverdue: true,
            totalNotYetDue: true,
          },
          alignment: 'right',
        },
        agingSummary: {
          enabled: true,
          buckets: {
            '1': true,
            '2': true,
            '3': true,
            '4': true,
            '5': true,
          },
          displayStyle: 'table',
          highlightOverdue: true,
        },
      },
      settingsTabSettings: {
        dateRange: {
          type: 'last-12-months',
          customFrom: undefined,
          customTo: undefined,
        },
        sendSettings: {
          overridePaperSize: false,
          paperSize: 'a4',
          filenameFormat: 'Statement_{{account_number}}_{{statement_date}}',
        },
      },
    },
  },
  {
    id: '4',
    name: 'Overdue Notice',
    type: 'email',
    subject: 'Overdue Payment Notice',
    content:
      '<p>Dear <<customerName>>,</p><p>Your account <<accountNumber>> is overdue. Please remit payment of <<accountBalance>> immediately.</p>',
    isDefault: false,
    category: ['overdue', 'collections'],
    variables: ['<<customerName>>', '<<accountNumber>>', '<<accountBalance>>'],
    lastModified: '2024-01-25T09:30:00Z',
    createdAt: '2024-01-12T10:00:00Z',
    createdBy: 'Admin User',
  },
  {
    id: '6',
    name: 'Payment Confirmation',
    type: 'email',
    subject: 'Payment Received - Thank You',
    content:
      '<p>Dear <<customerName>>,</p><p>We have received your payment of <<paymentAmount>>. Thank you!</p><p>Remaining balance: <<accountBalance>></p>',
    isDefault: false,
    category: ['payment', 'confirmation'],
    variables: ['<<customerName>>', '<<paymentAmount>>', '<<accountBalance>>'],
    lastModified: '2024-01-28T13:00:00Z',
    createdAt: '2024-01-15T10:00:00Z',
    createdBy: 'Admin User',
  },
  {
    id: '7',
    name: 'Final Notice',
    type: 'email',
    subject: 'FINAL NOTICE - Immediate Action Required - Account <<accountNumber>>',
    content:
      '<p><strong>FINAL NOTICE - IMMEDIATE ACTION REQUIRED</strong></p><p>Dear <<contactFirstName>>,</p><p>This is your <strong>FINAL NOTICE</strong> regarding the outstanding balance on your account <<accountNumber>>.</p><p><strong>Amount Due: <<accountBalance>></strong><br><strong>Days Overdue: <<daysOverdue>></strong></p><p>Despite our previous attempts to contact you, your account remains severely overdue. We must receive payment <strong>immediately</strong> to avoid further action.</p><p><strong>URGENT: Pay now using the secure link below:</strong><br><<paymentLink>></p><p><strong>If payment is not received within 48 hours, we will be forced to:</strong></p><ul><li>Suspend your account and all services</li><li>Report this matter to credit reference agencies, which will negatively impact your credit rating</li><li>Commence legal proceedings to recover the debt, including additional costs and interest</li><li>Engage external debt collection agencies</li></ul><p>This is your last opportunity to resolve this matter before we escalate to formal legal action. We strongly urge you to make payment immediately or contact us to discuss payment arrangements.</p><p>If you have already made payment, please disregard this notice and contact us immediately with proof of payment.</p><p><strong>Contact us urgently:</strong><br>Email: <<creditControllerEmail>><br>Phone: <<companyPhone>></p><p>Failure to respond will result in immediate escalation.</p><p><<creditControllerFirstName>><br>Credit Control Department</p>',
    isDefault: false,
    category: ['final', 'collections', 'urgent'],
    variables: [
      '<<contactFirstName>>',
      '<<accountNumber>>',
      '<<accountBalance>>',
      '<<daysOverdue>>',
      '<<paymentLink>>',
      '<<creditControllerEmail>>',
      '<<companyPhone>>',
      '<<creditControllerFirstName>>',
    ],
    lastModified: '2024-01-30T16:00:00Z',
    createdAt: '2024-01-30T16:00:00Z',
    createdBy: 'Admin User',
  },
];

export function useTemplatesList() {
  const { apiGet } = useApi();
  const { selectedCompany } = useCompany();
  const { isDemo } = useMode();

  const fixedParams: GetTemplatesParams = {
    page: 1,
    pageSize: 100,
    sortBy: 'Name',
    sortOrder: 'asc',
  };

  return useModeAwareQuery<Template[], Error>(
    ['templates', selectedCompany?.id],
    async ({ signal }) => {
      const response = await apiGet<GetTemplatesApiResponse>('/api/v1/templates', fixedParams, signal);
      return response.data.map(mapTemplateListItemApiToUi);
    },
    mockTemplates,
    {
      staleTime: 0,
      refetchOnMount: 'always',
      enabled: isDemo || !!selectedCompany?.id,
    },
  );
}

export function useTemplateById(templateId: string | null) {
  const { apiGet } = useApi();
  const mockTemplate = mockTemplates.find((t) => t.id === templateId) ?? null;

  return useModeAwareQuery<Template | null, Error>(
    ['template', templateId],
    async ({ signal }) => {
      if (!templateId) return null;
      const response = await apiGet<TemplateDetailApi>(`/api/v1/templates/${templateId}`, undefined, signal);
      return mapTemplateDetailApiToUi(response);
    },
    mockTemplate,
    {
      enabled: !!templateId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

export function useCreateTemplate({ forceLive = false }: { forceLive?: boolean } = {}) {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TemplateDetailApi, Error, Template>(
    async (data) => {
      const request: CreateTemplateApiRequest = mapTemplateUiToCreateRequest(data);
      return apiPost<TemplateDetailApi>('/api/v1/templates', request);
    },
    async (data) => {
      return {
        id: Date.now(),
        name: data.name,
        templateTypeId: data.type === 'statement' ? 2 : 1,
        templateTypeName: data.type === 'statement' ? 'Statement' : 'Email',
        subject: data.subject,
        content: data.content,
        isDefault: data.isDefault,
        category: data.category?.join(', '),
        variables: data.variables?.join(', '),
        createdAt: new Date().toISOString(),
        createdByUserName: 'Current User',
      } as TemplateDetailApi;
    },
    {
      forceLive,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['templates'] });
        queryClient.invalidateQueries({ queryKey: ['templates-graphql-list'] });
      },
    },
  );
}

export function useUpdateTemplate(templateId: string, { forceLive = false }: { forceLive?: boolean } = {}) {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TemplateDetailApi, Error, Template>(
    async (data) => {
      const request: UpdateTemplateApiRequest = mapTemplateUiToUpdateRequest(data);
      return apiPut<TemplateDetailApi>(`/api/v1/templates/${templateId}`, request);
    },
    async () => {
      return {
        id: Number(templateId),
        name: 'Updated Template',
        templateTypeId: 1,
        templateTypeName: 'Email',
        content: '',
        isDefault: false,
        createdAt: new Date().toISOString(),
      } as TemplateDetailApi;
    },
    {
      forceLive,
      onSuccess: () => {
        queryClient.removeQueries({
          queryKey: ['template', templateId],
          exact: false,
        });
        queryClient.invalidateQueries({ queryKey: ['templates'] });
        queryClient.invalidateQueries({ queryKey: ['templates-graphql-list'] });
      },
    },
  );
}

export function useDeleteTemplate() {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, string>(
    async (templateId) => {
      await apiDelete(`/api/v1/templates/${templateId}`);
    },
    async () => {
      return;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['templates'] });
        queryClient.invalidateQueries({ queryKey: ['templates-graphql-list'] });
      },
    },
  );
}

export function useSetDefaultTemplate() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TemplateDetailApi, Error, string>(
    async (templateId) => {
      return apiPost<TemplateDetailApi>(`/api/v1/templates/${templateId}/set-default`, {});
    },
    async () => {
      return {
        id: 0,
        name: '',
        templateTypeId: 2,
        templateTypeName: 'Statement',
        content: '',
        isDefault: true,
        createdAt: new Date().toISOString(),
      } as TemplateDetailApi;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['templates'] });
        queryClient.invalidateQueries({ queryKey: ['templates-graphql-list'] });
      },
    },
  );
}

export function useClearDefaultTemplate() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TemplateDetailApi, Error, string>(
    async (templateId) => {
      return apiPost<TemplateDetailApi>(`/api/v1/templates/${templateId}/clear-default`, {});
    },
    async () => {
      return {
        id: 0,
        name: '',
        templateTypeId: 2,
        templateTypeName: 'Statement',
        content: '',
        isDefault: false,
        createdAt: new Date().toISOString(),
      } as TemplateDetailApi;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['templates'] });
        queryClient.invalidateQueries({ queryKey: ['templates-graphql-list'] });
      },
    },
  );
}

// ─── Template Category Types (GraphQL) ─────────────────────────────────────

export interface TemplateCategoryType {
  id: number;
  code: string;
  name: string;
  description?: string;
}

interface TemplateCategoryTypeNode {
  templateCategoryTypeId: number;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface TemplateCategoryTypeListGraphQLResponse {
  templateCategoryTypes: {
    templateCategoryTypeList: {
      edges: Array<{ node: TemplateCategoryTypeNode }>;
    };
  };
}

const TEMPLATE_CATEGORY_TYPE_LIST_QUERY = `
  query TemplateCategoryTypeList($first: Int) {
    templateCategoryTypes {
      templateCategoryTypeList(first: $first) {
        edges {
          node {
            templateCategoryTypeId
            code
            name
            description
            sortOrder
            isActive
          }
        }
      }
    }
  }
`;

const mockTemplateCategoryTypes: TemplateCategoryType[] = [
  {
    id: 1,
    code: 'general',
    name: 'General',
    description: 'General purpose templates',
  },
  {
    id: 2,
    code: 'collections',
    name: 'Collections',
    description: 'Collection-related templates',
  },
  {
    id: 3,
    code: 'promises',
    name: 'Promises',
    description: 'Promise to pay templates',
  },
  {
    id: 4,
    code: 'customers',
    name: 'Customers',
    description: 'Customer communication templates',
  },
  {
    id: 5,
    code: 'transactions',
    name: 'Transactions',
    description: 'Transaction-related templates',
  },
  {
    id: 6,
    code: 'disputes',
    name: 'Disputes',
    description: 'Dispute-related templates',
  },
];

function mapNodeToTemplateCategoryType(node: TemplateCategoryTypeNode): TemplateCategoryType {
  return {
    id: node.templateCategoryTypeId,
    code: node.code,
    name: node.name,
    description: node.description ?? undefined,
  };
}

export function useTemplateCategoryTypes() {
  const apiConfig = useApiConfig();

  return useModeAwareQuery<TemplateCategoryType[], Error>(
    ['templateCategoryTypes'],
    async ({ signal }) => {
      const response = await graphqlQuery<TemplateCategoryTypeListGraphQLResponse>(
        apiConfig,
        TEMPLATE_CATEGORY_TYPE_LIST_QUERY,
        { first: 0 },
        signal,
      );

      return response.templateCategoryTypes.templateCategoryTypeList.edges
        .map((x) => mapNodeToTemplateCategoryType(x.node))
        .filter((x) => x.code !== undefined);
    },
    mockTemplateCategoryTypes,
    {
      staleTime: 5 * 60 * 1000,
    },
  );
}
